import React, { useEffect, useState, useReducer } from "react";
import { useAgentApi } from "../../../services/AgentApi";
import { useAppContext } from "../../../contexts/AppContext";
import Dialog from "carbon-react/lib/components/dialog";
import Heading from "carbon-react/lib/components/heading";
import { Row, Column } from "carbon-react/lib/components/row";
import TextBox from "carbon-react/lib/__experimental__/components/textbox";
import Link from "carbon-react/lib/components/link";
import Create from "carbon-react/lib/components/create";
import {
  Select,
  Option,
} from "carbon-react/lib/__experimental__/components/select";
import {
  agentRoles,
  getAllAgentRoles,
  agentEmailMaxLength,
} from "../../../constants/model";
import { v4 as uuidv4 } from "uuid";
import ErrorMessage from "../../messages/ErrorMessage";
import DialogConfirmButtons from "../../common/dialogConfirmButtons/DialogConfirmButtons";
import ConfirmDialog from "../../common/confirmDialog/ConfirmDialog";

import "./AgentInviteDialog.css";

const maxInviteBatchSize = 10;
const supportedEmailDomain = "@sage.com";
const maxEmailPrefixLength =
  (agentEmailMaxLength || 254) - supportedEmailDomain.length;
const illegalEmailChars = ["@", " "];

const roleMetadata = getAllAgentRoles();

const colMetadata = {
  agentEmail: { span: 5, align: "left" },
  agentRole: { span: 2, align: "left" },
  action: { span: 1, align: "center" },
};

const rowMetadata = {
  colCount:
    colMetadata.agentEmail.span +
    colMetadata.agentRole.span +
    colMetadata.action.span,
  gutter: "small",
};

const createEmptyInvite = () => {
  //  Agent role should default to operator
  return {
    id: uuidv4(),
    email: "",
    role: agentRoles.operator.value,
    error: null,
  };
};

const getInitialInvites = () => {
  return [createEmptyInvite()];
};

const removeIllegalCharsFromString = (illegalChars, value) => {
  if (!illegalChars || illegalChars.length <= 0) return value;

  for (let idx = 0; idx < illegalChars.length; idx++) {
    value = value.replace(illegalChars[idx], "");
  }

  return value;
};

const inviteReducer = (state, action) => {
  switch (action.type) {
    case "SET_EMAIL":
      state[action.index].error = null;

      const sanitizedEmail = removeIllegalCharsFromString(
        illegalEmailChars,
        action.value
      );

      if (sanitizedEmail && sanitizedEmail.length > maxEmailPrefixLength) {
        //  Don't allow email to exceed max length
        return [...state];
      }

      state[action.index].email = sanitizedEmail;
      return [...state];
    case "SET_ROLE":
      state[action.index].role = action.value;
      return [...state];
    case "REMOVE_INVITE":
      if (action.index >= 0) state.splice(action.index, 1);
      return [...state];
    case "ADD_INVITE":
      return [...state, createEmptyInvite()];
    case "RESET":
      return getInitialInvites();
    case "SANITIZE":
      return sanitizeInviteData(state);
    case "PROCESS_RESPONSES":
      return processResponses(state, action.responses);
    case "CLEAR_PREVIOUS_ERRORS":
      return state.map((invite) => {
        invite.error = null;
        return invite;
      });
    default:
      return state;
  }
};

const processResponses = (state, inviteResponses) => {
  if (!inviteResponses || inviteResponses.length < 1) return [...state];

  const inviteFailures = [];
  for (let idx = 0; idx < inviteResponses.length; idx++) {
    if (!inviteResponses[idx].success) {
      const failureResponse = inviteResponses[idx];
      const invite = state.find(
        (invite) => invite.id === failureResponse.inviteId
      );
      invite.error =
        typeof failureResponse.error === "string"
          ? failureResponse.error
          : "Unnexpected error occurred";
      inviteFailures.push(invite);
    }
  }
  return inviteFailures.length > 0
    ? [...inviteFailures]
    : [...getInitialInvites()];
};

const sanitizeInviteData = (state) => {
  //  Remove empty invites and duplicates (same email denotes duplicate)
  if (state && state.length > 0) {
    const sanitizedState = state.reduce((arr, item) => {
      let sanitizedEmail =
        item?.email && item?.email.length > 0 ? item.email.trim() : "";
      if (sanitizedEmail.length > 0) {
        item.email = sanitizedEmail;

        let isDuplicate = !!arr.find((x) => x.email === item.email);
        if (!isDuplicate) {
          arr.push(item);
        }
      }
      return arr;
    }, []);
    state = sanitizedState;
  }

  return state && state.length > 0 ? [...state] : getInitialInvites();
};

const AgentInviteDialog = ({ open, onInvitesCreated, onClose }) => {
  const { inviteAgent } = useAgentApi();
  const { agentEntitlement, toastSuccess } = useAppContext();
  const [dialogConfirmSaveOpen, setDialogConfirmSaveOpen] = useState(false);
  const [dialogConfirmCloseOpen, setDialogConfirmCloseOpen] = useState(false);
  const [noInvitesOnSaveDialogOpen, setNoInvitesOnSaveDialogOpen] = useState(
    false
  );
  const [atLeastOneInvitePresent, setAtLeastOneInvitePresent] = useState(false);
  const [invites, dispatch] = useReducer(inviteReducer, getInitialInvites());
  const [creatingInvites, setCreatingInvites] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    const atLeastOnePresent =
      invites &&
      invites.length > 0 &&
      invites.some((invite) => {
        return invite && invite.email && invite.email.trim().length > 0;
      });
    setAtLeastOneInvitePresent(atLeastOnePresent);
  }, [invites]);

  const createInvites = async () => {
    let encounteredError = false;

    dispatch({
      type: "CLEAR_PREVIOUS_ERRORS",
    });

    try {
      setCreatingInvites(true);

      const responses = [];

      if (invites && invites.length > 0) {
        for (let idx = invites.length - 1; idx >= 0; idx--) {
          const response = await createInvite(invites[idx]);
          if (response && response.error) encounteredError = true;
          responses.push(response);
        }
      }

      const successResponses = responses.filter((response) => {
        return response.success;
      });
      if (successResponses && successResponses.length > 0)
        onOneOrMoreInvitesCreated();

      dispatch({
        type: "PROCESS_RESPONSES",
        responses: responses,
      });
    } catch (error) {
      setShowError(true);
      encounteredError = true;
    }

    setCreatingInvites(false);

    return encounteredError;
  };

  const defaultInviteErrorMsg = "Failed to create agent invite";

  const createInvite = async (invite) => {
    let result = {
      inviteId: invite.id,
    };
    try {
      const fullEmail = `${invite.email}${supportedEmailDomain}`;
      const apiResult = await inviteAgent(fullEmail, invite.role);

      if (apiResult.success) {
        toastSuccess(`Invite created/updated for '${fullEmail}'.`);
      }

      result.success = apiResult.success;
      result.error = apiResult.success
        ? null
        : apiResult.error?.description || defaultInviteErrorMsg;
    } catch (error) {
      result.success = false;
      result.error = error ? error.toString() : defaultInviteErrorMsg;
    }
    return result;
  };

  const onOneOrMoreInvitesCreated = () => {
    if (onInvitesCreated && typeof onInvitesCreated === "function") {
      onInvitesCreated();
    }
  };

  const onDialogClose = () => {
    setShowError(false);
    dispatch({
      type: "RESET",
    });
    onClose();
  };

  const onDialogConfirmSaveCancel = () => {
    //  Cancel save action - remain on invite dialog
    setDialogConfirmSaveOpen(false);
  };

  const onDialogConfirmSaveAccept = async () => {
    //  Close the confirm dialog and invoke api operation
    setDialogConfirmSaveOpen(false);

    const encounteredError = await createInvites();

    //  We can't rely on atLeastOnePresent here (race issue)
    if (!encounteredError) {
      onDialogClose();
    }
  };

  const onDialogConfirmCloseCancel = () => {
    //  Cancel close action - remain on invite dialog
    setDialogConfirmCloseOpen(false);
  };

  const onDialogConfirmCloseAccept = () => {
    //  Accept close action - close invite dialog
    setDialogConfirmCloseOpen(false);
    onDialogClose();
  };

  const onNoInvitesOnSaveDialogClose = () => {
    setNoInvitesOnSaveDialogOpen(false);
  };

  const onSave = () => {
    // Don't allow form closure during invite creation
    if (creatingInvites) return;

    dispatch({
      type: "SANITIZE",
    });

    if (!atLeastOneInvitePresent) {
      setNoInvitesOnSaveDialogOpen(true);
      return;
    }

    setDialogConfirmSaveOpen(true);
  };

  const onCancel = () => {
    // Don't allow form closure during invite creation
    if (creatingInvites) return;

    if (atLeastOneInvitePresent) {
      //  Theres data present, ensure they wish to close
      setDialogConfirmCloseOpen(true);
      return;
    }
    onDialogClose();
  };

  const onEmailChange = (idx, email) => {
    dispatch({
      type: "SET_EMAIL",
      index: idx,
      value: email,
    });
  };

  const onRoleChange = (idx, role) => {
    dispatch({
      type: "SET_ROLE",
      index: idx,
      value: role,
    });
  };

  const onRemoveInvite = (idx) => {
    if (creatingInvites === false)
      dispatch({
        type: "REMOVE_INVITE",
        index: idx,
      });
  };

  const onAddInvite = () => {
    if (creatingInvites === false)
      dispatch({
        type: "ADD_INVITE",
      });
  };

  const getValidInviteRoles = () => {
    return roleMetadata.filter(
      (role) =>
        agentEntitlement &&
        agentEntitlement.agents.administrableRoles.includes(role.value)
    );
  };

  const agentInviteRows = invites.map((invite, idx) => {
    return (
      <Row
        key={`row-${idx}`}
        columns={rowMetadata.colCount.toString()}
        gutter={rowMetadata.gutter}
      >
        <Column
          columnAlign={colMetadata.agentEmail.align}
          columnSpan={colMetadata.agentEmail.span.toString()}
        >
          <TextBox
            disabled={creatingInvites}
            placeholder={"Sage employee email address..."}
            value={invite.email}
            onChange={(e) => onEmailChange(idx, e.target.value)}
            autoFocus
            {...(invite.error ? { hasError: true } : {})}
            {...(invite.error ? { inputIcon: "error" } : {})}
            {...(invite.error ? { tooltipMessage: invite.error } : {})}
          >
            <div className="agent-invite-dlg-email-suffix">
              <strong>{supportedEmailDomain}</strong>
            </div>
          </TextBox>
        </Column>
        <Column
          columnAlign={colMetadata.agentRole.align}
          columnSpan={colMetadata.agentRole.span.toString()}
        >
          <Select
            disabled={creatingInvites}
            preventFocusAutoOpen={true}
            filterable={false}
            value={invite.role}
            onChange={(e) => onRoleChange(idx, e.target.value[0].optionValue)}
          >
            {getValidInviteRoles().map((role) => {
              return (
                <Option
                  key={`option-${role.value}`}
                  text={role.displayValue}
                  value={role.value}
                />
              );
            })}
          </Select>
        </Column>
        {invites.length > 1 ? (
          <Column
            columnAlign={colMetadata.action.align}
            columnSpan={colMetadata.action.span.toString()}
          >
            <Link
              className="agent-invite-dlg-remove-invite-link"
              onClick={() => onRemoveInvite(idx)}
            >
              <strong>Remove</strong>
            </Link>
          </Column>
        ) : null}
      </Row>
    );
  });

  const headerRow = (
    <Row columns={rowMetadata.colCount.toString()} gutter={rowMetadata.gutter}>
      <Column
        columnAlign={colMetadata.agentEmail.align}
        columnSpan={colMetadata.agentEmail.span.toString()}
      >
        <strong>Agent email address</strong>
      </Column>
      <Column
        columnAlign={colMetadata.agentRole.align}
        columnSpan={colMetadata.agentRole.span.toString()}
      >
        <strong>Role</strong>
      </Column>
    </Row>
  );

  const addInviteRow = (
    <>
      {invites.length < maxInviteBatchSize ? (
        <Row
          columns={rowMetadata.colCount.toString()}
          gutter={rowMetadata.gutter}
        >
          <Column columnAlign="left" columnSpan="1">
            <Create onClick={() => onAddInvite()}>
              <strong>Add invite</strong>
            </Create>
          </Column>
        </Row>
      ) : null}
    </>
  );

  const buttonRow = (
    <DialogConfirmButtons
      appearBusy={creatingInvites}
      confirmBtnEnabled={true}
      confirmBtnLabel="Create Invites"
      onConfirm={onSave}
      onCancel={onCancel}
    />
  );

  const errorMessage = (
    <>
      {showError !== true ? null : (
        <ErrorMessage message={"Failed to process invites"} />
      )}
    </>
  );

  return (
    <>
      <Dialog
        open={open}
        showCloseIcon={true}
        onCancel={onCancel}
        size="medium-large"
      >
        <Heading title="Invite Agents" />
        {headerRow}
        {agentInviteRows}
        {addInviteRow}
        {buttonRow}
        {errorMessage}
      </Dialog>
      <ConfirmDialog
        open={dialogConfirmCloseOpen}
        prompt="It looks like you've entered one or more agent invites. Are you sure
        you want to abandon these?"
        confirmBtnLabel="Abandon"
        onConfirm={onDialogConfirmCloseAccept}
        onCancel={onDialogConfirmCloseCancel}
      />
      <ConfirmDialog
        open={dialogConfirmSaveOpen}
        prompt={`You're about to create ${
          invites && invites.length > 0 ? invites.length : 0
        } agent invite(s).  Are you sure you want to continue?`}
        confirmBtnLabel="Continue"
        onConfirm={onDialogConfirmSaveAccept}
        onCancel={onDialogConfirmSaveCancel}
      />
      <ConfirmDialog
        open={noInvitesOnSaveDialogOpen}
        prompt="You must provide at least one invite!"
        confirmBtnLabel="Continue"
        onConfirm={onNoInvitesOnSaveDialogClose}
        omitCancelBtn={true}
        onCancel={onNoInvitesOnSaveDialogClose}
      />
    </>
  );
};

export default AgentInviteDialog;
